import { Injectable, NgZone, Inject } from "@angular/core";
import { BehaviorSubject, Observable, lastValueFrom } from "rxjs";
import { Configuration } from "@/angular-blocworx/components/configuration/services/configuration.service";
import { HttpClient } from "@angular/common/http";

declare const google: any; // Declare google global object for TypeScript

interface GoogleUser {
    id: string;
    name: string;
    email: string;
    imageUrl: string;
    idToken: string; // The token to send to backend
}

@Injectable({
    providedIn: "root",
})

export class GoogleAuthService {
    private _isLoggedIn = new BehaviorSubject<boolean>(false);
    private _user = new BehaviorSubject<GoogleUser | null>(null);
    private _isGoogleAuthInitialized = new BehaviorSubject<boolean>(false);

    public isLoggedIn$: Observable<boolean> = this._isLoggedIn.asObservable();
    public user$: Observable<GoogleUser | null> = this._user.asObservable();
    public isGoogleAuthInitialized$: Observable<boolean> = this._isGoogleAuthInitialized.asObservable();
    public apiUrl: string;

    constructor(
        @Inject(NgZone) private ngZone: NgZone,
        @Inject(Configuration) private configuration: Configuration,
        @Inject(HttpClient) private http: HttpClient
    ) {
        this.apiUrl = this.configuration.getApiUrl();
        this.initializeGoogleAuth(); // Call a new initialization method
    }

    private async initializeGoogleAuth(): Promise<void> {
        const clientId = await this.retrieveClientId();
        if (clientId) {
            this.initGoogleSignIn(clientId);
            this._isGoogleAuthInitialized.next(true); // Signal that GIS is now initialized
        } else {
            console.error("Failed to retrieve Google Client ID. Google Sign-In will not be initialized.");
            this._isGoogleAuthInitialized.next(false); // Signal failure to initialize
        }
    }

    /**
     * Initializes Google Identity Services (GIS) client - This code was adapted from Google's GIS documentation.
     * This should be called only once.
     */
    initGoogleSignIn(clientId: string): void {
        if (typeof google === "undefined" || !google.accounts || !google.accounts.id) {
            console.warn(
                'Google Identity Services (GIS) library is not loaded. Ensure <script src="https://accounts.google.com/gsi/client" async defer></script> is in your index.html.'
            );
            return;
        }

        // const CLIENT_ID = "539709530722-jdun5tgfcdfjjgj23l9j9llkfa0qj5m5.apps.googleusercontent.com";

        google.accounts.id.initialize({
            client_id: clientId,
            // The callback function that handles the credential response
            callback: (response: any) => this.ngZone.run(() => this.handleCredentialResponse(response)),
            auto_select: false, // Prevents automatic sign-in on a page load
            cancel_on_tap_outside: true, // Allows users to dismiss the one-tap prompt
            context: "signin", // 'signin', 'signup', 'use'
            ux_mode: "popup", // 'popup' for the current window or 'redirect' for full page redirect
        });
    }

    /**
     * Adapted from Google's GIS documentation.
     * Handles the credential response from Google after a successful sign-in.
     * This is called by the GIS library.
     * @param response The credential response object from Google.
     */
    private handleCredentialResponse(response: any): void {
        if (response.credential) {
            const idToken = response.credential;
            const profile = this.decodeJwt(idToken); // Decode the JWT to get user info

            if (profile) {
                const user: GoogleUser = {
                    id: profile.sub, // Google user ID
                    name: profile.name,
                    email: profile.email,
                    imageUrl: profile.picture,
                    idToken: idToken, // The full ID token to send to backend for verification
                };
                this._user.next(user);
                this._isLoggedIn.next(true);

                this.verifyGoogleIdTokenWithBackend(idToken).subscribe({
                    next: (backendResponse: any) => {
                        console.log("Backend verification successful:", backendResponse);

                        // Store the JWT token from backend in localStorage
                        if (backendResponse.token) {
                            localStorage.setItem("satellizer_token", backendResponse.token);
                            console.log("Stored JWT token from backend.");
                        } else {
                            console.warn("Backend response did not contain a 'token'.");
                        }

                        // Redirect the browser window to the intended app entry point.
                        if (backendResponse.redirect_url) {
                            console.log("Redirecting browser to:", backendResponse.redirect_url);
                            // Using window.location.href to trigger a full page reload,
                            // which is necessary for AngularJS to re-evaluate its state.
                            window.location.href = backendResponse.redirect_url;
                        } else {
                            // If no redirect_url, go to a default, or just reload the current page.
                            console.warn("Backend response did not contain a 'redirect_url'. Defaulting to '/'.");
                            window.location.href = "/";
                        }
                    },
                    error: (error: any) => {
                        console.error("Backend verification failed:", error);
                        this.signOut();
                        // clear the satellizer_token if it was set incorrectly.
                        localStorage.removeItem("satellizer_token");
                    },
                    complete: () => {
                        console.log("Verification stream completed");
                    },
                });
            } else {
                console.error("Failed to decode Google ID token.");
                this._isLoggedIn.next(false);
                this._user.next(null);
            }
        } else {
            // User cancelled or sign-in failed
            console.error("Google Sign-In failed or cancelled:", response);
            this._isLoggedIn.next(false);
            this._user.next(null);
        }
    }

    /**
     * Signs out the user from Google's session - This code was adapted from Google's GIS documentation.
     */
    signOut(): void {
        if (google && google.accounts && google.accounts.id) {
            // This will disable auto-select for later visits and clear the current Google session in the browser.
            google.accounts.id.disableAutoSelect();
            google.accounts.id.revoke(this._user.value?.id, (response: any) => {
                this.ngZone.run(() => {
                    // Ensure Angular detects changes
                    console.log("Google account revoked/signed out:", response);
                    this._isLoggedIn.next(false);
                    this._user.next(null);
                    // TODO: clear backend session if necessary
                });
            });
        } else {
            this.ngZone.run(() => {
                console.warn("Google API not loaded or user not logged in for signOut.");
                this._isLoggedIn.next(false); // Force state change
                this._user.next(null);
            });
        }
    }

    /**
     * Decode the JWT (ID Token) payload - This code was adapted from Google's GIS documentation.
     * @param token The JWT string.
     * @returns The decoded payload object.
     */
    private decodeJwt(token: string): any {
        try {
            const base64Url = token.split(".")[1];
            const base64 = base64Url.replace(/-/g, "+").replace(/_/g, "/");
            const jsonPayload = decodeURIComponent(
                atob(base64)
                    .split("")
                    .map(function (c) {
                        return "%" + ("00" + c.charCodeAt(0).toString(16)).slice(-2);
                    })
                    .join("")
            );
            return JSON.parse(jsonPayload);
        } catch (e) {
            console.error("Error decoding JWT:", e);
            return null;
        }
    }

    /**
     * Sends the Google ID Token to the Laravel backend for verification and authentication.
     * @param idToken The Google ID Token string.
     * @returns Observable of the backend response.
     */
    private verifyGoogleIdTokenWithBackend(idToken: string): Observable<any> {
        const backendUrl = `${this.apiUrl}authenticate-with-google`;
        return this.http.post(backendUrl, { id_token: idToken });
    }

    private async retrieveClientId(): Promise<string | null> {
        try {
            // Try to get from localStorage
            const appSettingsRaw = localStorage.getItem("appSettings");
            if (appSettingsRaw) {
                const appSettings = JSON.parse(appSettingsRaw);
                const googleSettings = appSettings.google;
                if (googleSettings && Array.isArray(googleSettings.data)) {
                    const clientIdObj = googleSettings.data.find((item: any) => item.setting_key === "client_id" && !!item.setting_value);
                    if (clientIdObj && clientIdObj.setting_value) {
                        console.log("Google Client ID retrieved from localStorage.");
                        return clientIdObj.setting_value;
                    }
                }
            }
            // If not found, fetch from API
            const apiEndpoint = this.apiUrl + "google-sign-in-app-settings";
            const response: any = await lastValueFrom(this.http.get(apiEndpoint));

            if (response && response.data && Array.isArray(response.data)) {
                const clientIdObj = response.data.find((item: any) => item.setting_key === "client_id" && !!item.setting_value);
                if (clientIdObj && clientIdObj.setting_value) {
                    console.log("Google Client ID retrieved from API.");
                    return clientIdObj.setting_value;
                }
            }
            console.warn("Google Client ID not found.");
            return null;
        } catch (e) {
            console.error("Error retrieving Google Client ID:", e);
            return null;
        }
    }
}
